/**
 * DOM-based "new version available" modal. We use a DOM overlay rather than
 * a Phaser scene overlay because (a) the version check lives outside the
 * scene graph, (b) we want it to sit above the canvas unconditionally, and
 * (c) it only ever shows once per session, so keeping it out of the render
 * loop is free.
 *
 * Styling intentionally mirrors the in-game PauseOverlay palette
 * (`#1a1a2e` panel, `#4a4a7a` stroke, `#ccccee` title, green primary button)
 * so it feels native to the game.
 */

import { reloadToVersion } from './version-check';

let modalRoot: HTMLDivElement | null = null;

export function showVersionUpdateModal(remoteVersion: string): void {
  // Defensive: never stack modals.
  if (modalRoot) return;

  const root = document.createElement('div');
  root.setAttribute('data-pxflow-modal', 'version-update');
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:10000',
    'background:rgba(0,0,0,0.7)',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-family:"Outfit",system-ui,sans-serif',
    'padding:24px',
    // Modal overlay should swallow all pointer events so underlying canvas
    // input cannot leak through (game is paused, but belt-and-braces).
    'touch-action:none',
  ].join(';');

  const panel = document.createElement('div');
  panel.style.cssText = [
    'background:#1a1a2e',
    'border:4px solid rgba(74,74,122,0.6)',
    'border-radius:32px',
    'padding:48px 40px 40px',
    'max-width:480px',
    'width:100%',
    'text-align:center',
    'box-shadow:0 16px 48px rgba(0,0,0,0.5)',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = 'Update Available';
  title.style.cssText = [
    'color:#ccccee',
    'font-size:36px',
    'font-weight:700',
    'margin-bottom:20px',
    'letter-spacing:0.5px',
  ].join(';');

  const body = document.createElement('div');
  body.textContent = 'A new version of pxFlow is available.';
  body.style.cssText = [
    'color:#aaaacc',
    'font-size:20px',
    'line-height:1.4',
    'margin-bottom:36px',
  ].join(';');

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload Now';
  button.style.cssText = [
    'background:#2a4a2a',
    'color:#88cc88',
    'border:3px solid rgba(58,106,58,0.9)',
    'border-radius:20px',
    'font-family:inherit',
    'font-size:26px',
    'font-weight:700',
    'padding:18px 40px',
    'width:100%',
    'cursor:pointer',
    '-webkit-tap-highlight-color:transparent',
    'touch-action:manipulation',
  ].join(';');

  button.addEventListener('click', () => {
    reloadToVersion(remoteVersion);
  });

  panel.appendChild(title);
  panel.appendChild(body);
  panel.appendChild(button);
  root.appendChild(panel);
  document.body.appendChild(root);
  modalRoot = root;

  // Focus the button so Enter/Space triggers it for keyboard users.
  // queueMicrotask defer so the append-to-DOM settles first.
  queueMicrotask(() => button.focus());
}
